import React from 'react';
import { Link } from 'react-router-dom';

import titleImg from '../../assets/img/logo-lockup.svg';

// Public support page (https://italiannaturally.com/support) — linked from
// App Store Connect / Google Play as the app's support URL. Covers how to
// reach us for cancellations, refunds, feedback, and complaints. Reachable
// without authentication.

const LAST_UPDATED = 'June 11, 2026';
const SUPPORT_EMAIL = 'abogaard.dev@gmail.com';

const FONT_BODY = '"Hanken Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const FONT_DISPLAY = '"Fredoka", system-ui, sans-serif';

const styles = {
	page: { fontFamily: FONT_BODY, color: '#352F27', lineHeight: 1.6, backgroundColor: '#FBF7F0', minHeight: '100vh' },
	header: {
		display: 'flex', alignItems: 'center', justifyContent: 'space-between',
		gap: 16, maxWidth: 760, margin: '0 auto', padding: '24px 24px 0',
	},
	logo: { height: 34 },
	back: { color: '#D86641', fontWeight: 700, textDecoration: 'none' },
	container: { maxWidth: 760, margin: '0 auto', padding: '8px 24px 64px' },
	h1: { fontFamily: FONT_DISPLAY, fontSize: 34, fontWeight: 600, marginBottom: 4, color: '#1E1A15' },
	updated: { color: '#6B5F4D', marginTop: 0 },
	h2: { fontFamily: FONT_DISPLAY, fontSize: 24, fontWeight: 600, marginTop: 36, color: '#1E1A15' },
	a: { color: '#D86641' },
	contact: {
		background: '#FBF0EB', borderLeft: '4px solid #D86641', borderRadius: 12,
		padding: '4px 20px', margin: '24px 0',
	},
	email: { fontFamily: FONT_DISPLAY, fontSize: 20, fontWeight: 600 },
};

const Support = () => {
	return (
		<div style={styles.page}>
			<div style={styles.header}>
				<Link to="/">
					<img src={titleImg} alt="Italian Naturally" style={styles.logo} />
				</Link>
				<Link to="/" style={styles.back}>← Home</Link>
			</div>

			<div style={styles.container}>
				<h1 style={styles.h1}>Support</h1>
				<p style={styles.updated}>Last updated: {LAST_UPDATED}</p>

				<p>
					Need a hand with <strong>Italian Naturally</strong>? We&apos;re a small
					team and read every message. The fastest way to reach us — for anything
					below — is email:
				</p>

				<div style={styles.contact}>
					<p style={{ marginBottom: 4 }}><strong>Contact us</strong></p>
					<p style={styles.email}>
						<a style={styles.a} href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
					</p>
					<p>
						We aim to reply within <strong>2 business days</strong>. Please write from
						the email address on your account and include your device (iPhone / Android)
						so we can help faster.
					</p>
				</div>

				<h2 style={styles.h2}>Cancellations &amp; refunds</h2>
				<p>
					Subscriptions are billed by the <strong>App Store</strong> or{' '}
					<strong>Google Play</strong>, so cancellations and refunds are handled there:
				</p>
				<ul>
					<li>
						<strong>iPhone / iPad:</strong> open <em>Settings → [your name] →
						Subscriptions</em>, select Italian Naturally, and tap <em>Cancel
						Subscription</em>. Refunds: <a style={styles.a} href="https://reportaproblem.apple.com">reportaproblem.apple.com</a>.
					</li>
					<li>
						<strong>Android:</strong> open the <em>Google Play Store → Profile →
						Payments &amp; subscriptions → Subscriptions</em>, select Italian Naturally,
						and tap <em>Cancel</em>. Refunds: <a style={styles.a} href="https://play.google.com/store/account/subscriptions">play.google.com/store</a>.
					</li>
				</ul>
				<p>
					Your access continues until the end of the paid period. Note that{' '}
					<strong>deleting your account does not cancel a store subscription</strong> —
					cancel it in the store first. If you&apos;re stuck, email us and we&apos;ll
					point you the right way.
				</p>

				<h2 style={styles.h2}>Feedback &amp; feature requests</h2>
				<p>
					Got an idea, a conversation you&apos;d love to see, or something that felt
					off? We genuinely want to hear it. Email{' '}
					<a style={styles.a} href={`mailto:${SUPPORT_EMAIL}?subject=Feedback`}>{SUPPORT_EMAIL}</a>{' '}
					with the subject <em>“Feedback.”</em>
				</p>

				<h2 style={styles.h2}>Bugs &amp; complaints</h2>
				<p>
					If something isn&apos;t working — audio won&apos;t play, a score looks wrong,
					a purchase didn&apos;t unlock — tell us what happened and what you expected,
					with your device and (if you can) a screenshot. Email{' '}
					<a style={styles.a} href={`mailto:${SUPPORT_EMAIL}?subject=Problem%20report`}>{SUPPORT_EMAIL}</a>{' '}
					with the subject <em>“Problem report.”</em> We take complaints seriously and
					will work to make it right.
				</p>

				<h2 style={styles.h2}>Account &amp; privacy</h2>
				<p>
					You can delete your account anytime from <em>Profile → Delete account</em> in
					the app, or see <Link to="/delete_account" style={styles.a}>how to delete your
					account</Link>. See also our <Link to="/privacy" style={styles.a}>Privacy
					Policy</Link> and <Link to="/terms" style={styles.a}>Terms</Link>.
				</p>
			</div>
		</div>
	);
};

export default Support;
